import { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import { styled } from '@mui/material/styles'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import IconButton from '@mui/material/IconButton'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { colorPalette } from '../../view/style/colorPalette'
import { Title, MultilineText, Text } from '../../view/components/typography'
import { SUPPORT_SITE_PAGES, SupportSitePage } from './support-site-guide/pages'
import { DEMO_LIMITATION_MESSAGE } from './layout/DemoLimitationNotice'
import flagxsDemoLogo from './layout/flagxs_demo_logo.png'

// デモ画面の初期表示時に、その画面の概要と、操作別の説明ページへのリンクを案内する。
// デモは初見の来訪者向けなので、閉じたことを記憶せず画面を開くたびに毎回表示する
// （localStorage等での既読管理は意図的に持たない）。
//
// 画面中央に大きく出して最初に必ず読ませたい案内のため、Popoverではなく
// Dialogを使う。MUIのPopoverはbackdropを常にinvisible固定で描画するため
// （Popover.js内のadditionalPropsでslotProps.backdrop.invisibleを上書き）、
// 背後を暗くして視線を集めることができない。
//
// サポートサイトへの遷移はwindow.openを使わずaタグで行う。デモ画面では
// useDisableExternalNavigationがwindow.openを一括でno-opにしており、そこを
// 個別に許可リスト化しない方針のため（同フックのコメント参照）、
// window.openを経由しない手段でこちらが定義したURLへの遷移のみを成立させる。
type DemoSupportSiteGuideProps = {
  page: SupportSitePage
}

// 画面中央で確実に読ませるための大きさ。狭い画面でも収まるよう上限は
// ビューポート基準にする。
const CONTENT_WIDTH = 'min(560px, calc(100vw - 80px))'
const CONTENT_MAX_HEIGHT = 'calc(100vh - 80px)'

// maxWidth/marginの既定（Dialogのsmブレークポイント幅と外周余白）は
// Content側で幅と余白を決める設計と競合するため無効化する。
const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    margin: 0,
    maxWidth: 'none',
  },
})

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: CONTENT_WIDTH,
  maxHeight: CONTENT_MAX_HEIGHT,
  padding: '32px 40px 40px',
})

const Header = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
})

// タイトルとその直下の断り書きをひとまとまりとして扱い、閉じるボタンとの
// 間隔はHeaderのgapに委ねる。
const HeaderTexts = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 0,
})

// Titleの2l（20px）はfontWeight 500のため、typographyの定義に太字版がない。
// この案内では見出しを強調したいので、サイズはそのままに太さだけ上書きする。
const BoldTitle = styled(Title)({
  fontWeight: 700,
})

const TitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
})

// タイトル文字（20px）と釣り合う大きさ。ロゴは余白込みの画像なので、
// 見た目の大きさを揃えるため文字より一回り大きくとる。
const TitleLogo = styled('img')({
  width: '28px',
  height: '28px',
  flexShrink: 0,
})

// 説明文はsupport-site-guide/の定義側の\nをそのまま改行として見せる
// （プロジェクト既存のpre-line指定と同じ方針）。
const Description = styled(MultilineText)({
  whiteSpace: 'pre-line',
})

// リンクは画面ごとに件数が異なり増えていく想定のため、多くなっても
// ダイアログが画面をはみ出さないようこの領域だけスクロールさせる。
const LinkList = styled('ul')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  overflowY: 'auto',
})

const SupportSiteLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: 'fit-content',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    textDecorationColor: colorPalette.skyBlue[8],
  },
})

const LimitationNote = styled(MultilineText)({
  flexShrink: 0,
})

// URL直接入力で別のデモ画面へ移ると、各画面は同じ位置でこのコンポーネントを
// 描画するためReactが同一要素とみなし、stateが引き継がれる。前の画面で閉じた
// 状態のまま次の画面の案内が出なくなるのを防ぐため、画面キーでkeyを分ける。
export const DemoSupportSiteGuide = (props: DemoSupportSiteGuideProps) => (
  <SupportSiteGuideDialog key={props.page} {...props} />
)

const TITLE_ELEMENT_ID = 'demo-support-site-guide-title'

const SupportSiteGuideDialog = ({ page }: DemoSupportSiteGuideProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const { title, description, links } = SUPPORT_SITE_PAGES[page]

  return (
    <StyledDialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby={TITLE_ELEMENT_ID}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } },
      }}
    >
      <Content>
        <Header>
          <HeaderTexts>
            <TitleRow>
              <TitleLogo src={flagxsDemoLogo} alt="" />
              <BoldTitle variant="2l" id={TITLE_ELEMENT_ID}>
                {title}
              </BoldTitle>
            </TitleRow>
            <LimitationNote variant="mMedium" color={colorPalette.monotone[10]}>
              {`※${DEMO_LIMITATION_MESSAGE}`}
            </LimitationNote>
          </HeaderTexts>
          <IconButton
            size="small"
            onClick={() => setIsOpen(false)}
            aria-label="閉じる"
          >
            <CloseRoundedIcon
              sx={{ fontSize: 22, color: colorPalette.monotone[4] }}
            />
          </IconButton>
        </Header>
        <Description variant="m" color={colorPalette.monotone[5]}>
          {description}
        </Description>
        <LinkList>
          {links.map(({ label, url }) => (
            <li key={url}>
              <SupportSiteLink
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text variant="mMedium" color={colorPalette.skyBlue[8]}>
                  {label}
                </Text>
                <OpenInNewRoundedIcon
                  sx={{ fontSize: 18, color: colorPalette.skyBlue[8] }}
                />
              </SupportSiteLink>
            </li>
          ))}
        </LinkList>
      </Content>
    </StyledDialog>
  )
}
